import type { components } from "./generated-schema";
import { getAuthenticationSession } from "../auth/session";

export type ProblemDetails = components["schemas"]["ProblemDetail"];

type EnvelopeData = Readonly<{
  success: boolean;
  code: string;
  message: string;
  data: unknown;
  meta: unknown;
  requestId: string;
  timestamp: string;
}>;

export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails,
  ) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = "ApiProblem";
  }
}

export class ApiContractError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiContractError";
  }
}

export function createRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isSafeToRetry(init: RequestInit): boolean {
  const method = (init.method ?? "GET").toUpperCase();
  if (method === "GET" || method === "HEAD") return true;
  return new Headers(init.headers).has("Idempotency-Key");
}

function buildHeaders(init: RequestInit, token: string): Headers {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json, application/problem+json");
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Request-Id", headers.get("X-Request-Id") ?? createRequestId());
  if (init.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return headers;
}

async function execute(path: string, init: RequestInit, token: string): Promise<Response> {
  const headers = buildHeaders(init, token);
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  return fetch(`${baseUrl}${path}`, { ...init, headers });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireMediaType(response: Response, expected: string): void {
  const mediaType = response.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase();
  if (mediaType !== expected) {
    throw new ApiContractError(
      `Expected ${expected} response, got ${mediaType ?? "none"}`,
      response.status,
      response.headers.get("X-Request-Id") ?? undefined,
    );
  }
}

function isUtcTimestamp(value: unknown): value is string {
  return typeof value === "string" && !Number.isNaN(Date.parse(value));
}

function assertSuccessEnvelope(body: unknown, status: number, requestId: string | undefined): void {
  if (!isObject(body)) {
    throw new ApiContractError("Success response body must be a JSON object", status, requestId);
  }
  if (body.success !== true) {
    throw new ApiContractError("Success response must declare success=true", status, requestId);
  }
  if (typeof body.code !== "string" || !body.code) {
    throw new ApiContractError("Success response is missing a non-empty code", status, requestId);
  }
  if (typeof body.message !== "string" || !body.message) {
    throw new ApiContractError("Success response is missing a non-empty message", status, requestId);
  }
  if (typeof body.requestId !== "string" || !body.requestId) {
    throw new ApiContractError("Success response is missing a requestId", status, requestId);
  }
  if (!isUtcTimestamp(body.timestamp)) {
    throw new ApiContractError("Success response has an invalid timestamp", status, requestId);
  }
  if (!("data" in body) || body.data === null || body.data === undefined) {
    throw new ApiContractError("Success response is missing non-null data", status, requestId);
  }
}

function assertProblemShape(body: unknown, status: number, requestId: string | undefined): asserts body is ProblemDetails {
  if (!isObject(body)) {
    throw new ApiContractError("Problem response body must be a JSON object", status, requestId);
  }
  const required: Array<keyof ProblemDetails> = [
    "type",
    "title",
    "status",
    "code",
    "detail",
    "instance",
    "requestId",
    "timestamp",
  ];
  for (const field of required) {
    if (typeof body[field] !== "string" && field !== "status") {
      throw new ApiContractError(`Problem response is missing ${field}`, status, requestId);
    }
  }
  if (typeof body.status !== "number") {
    throw new ApiContractError("Problem response is missing a numeric status", status, requestId);
  }
  if (body.status !== status) {
    throw new ApiContractError(
      "Problem response status must equal the HTTP status",
      status,
      requestId,
    );
  }
  if (!isUtcTimestamp(body.timestamp)) {
    throw new ApiContractError("Problem response has an invalid timestamp", status, requestId);
  }
}

async function parseProblem(response: Response): Promise<ApiProblem> {
  const requestId = response.headers.get("X-Request-Id") ?? undefined;
  requireMediaType(response, "application/problem+json");
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiContractError("Problem response is not valid JSON", response.status, requestId);
  }
  try {
    assertProblemShape(body, response.status, requestId);
  } catch (error) {
    if (error instanceof ApiContractError) {
      throw error;
    }
    throw new ApiContractError("Malformed Problem response", response.status, requestId);
  }
  return new ApiProblem(response.status, body);
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await apiRequestWithMeta<T, unknown>(path, init)).data;
}

/**
 * Như {@link apiRequest} nhưng trả CẢ `meta` của envelope.
 *
 * <p>Cần cho các operation phân trang kiểu S11 (`searchMartyrs`/`searchCemeteries`/
 * `searchGraves`): `data` của chúng chỉ mang `rows`, còn `PageMeta`
 * (`page`/`size`/`totalElements`/`totalPages`) nằm ở tầng envelope. Không có helper này
 * thì `total` không tới được `Pagination` phía UI, dù backend đã gửi.</p>
 *
 * <p>Hợp đồng không đổi: vẫn kiểm media type, envelope `success` và Problem Details như
 * {@link apiRequest}. `meta` nullable theo contract (`ApiResponse.meta`) nên kiểu trả về
 * là `M | null`; caller phân trang truyền `PageMeta` và kiểm null tại chỗ dùng.</p>
 *
 * @param path đường dẫn tương đối tới base URL
 * @param init tuỳ chọn fetch (signal, method, headers, body)
 * @returns `data` đã unwrap cùng `meta` của envelope
 */
export async function apiRequestWithMeta<T, M>(
  path: string,
  init: RequestInit = {},
): Promise<{ readonly data: T; readonly meta: M | null }> {
  const authentication = getAuthenticationSession();
  let response = await execute(path, init, await authentication.getAccessToken());
  if (response.status === 401 && isSafeToRetry(init)) {
    response = await execute(path, init, await authentication.forceRefresh());
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return { data: undefined as T, meta: null };
  }
  requireMediaType(response, "application/json");
  const envelope: unknown = await response.json();
  assertSuccessEnvelope(envelope, response.status, response.headers.get("X-Request-Id") ?? undefined);
  const parsed = envelope as EnvelopeData & { meta?: unknown };
  return { data: parsed.data as T, meta: (parsed.meta ?? null) as M | null };
}